// Factory initial state for URX22 in Standard mode, laid out as the URX44V
// capture (initial-urx44v.ts) remapped to the URX22's channel layout:
//   - URX22 has 2 mono channels (CH1/2) instead of 4, and its 4 stereo channels
//     are CH3/4, 5/6, 7/8, 9/10 (build.ts shifts the stereo numbering with the
//     mono count). Stereo defaults are copied by POSITION among the stereo group,
//     so URX22 CH3/4 <- URX44V CH5/6, CH5/6 <- CH7/8, CH7/8 <- CH9/10, CH9/10 <- CH11/12.
//   - No LINE OUT and no microSD on URX22, so the MIX 1 -> LINE OUT default
//     patch is dropped and no SD nodes are seeded.
//   - The input stage is kept per jack and per source. URX22 has one MIC/LINE pair, whose
//     second jack carries Hi-Z (HI_Z_JACKS in params.ts), and the digital sources it has
//     take the URX44V's factory D.Gain.
// Values not affected by the model differences are the URX44V's. A new plan written to a
// factory-initialised URX22 leaves every address it writes at the value the unit already
// holds, the sample rate aside: with Follow USB on, the unit runs at the USB host's rate.
// The INS FX values live with the code that uses them (insertFxControl in
// core/control/translate.ts, the anchors in core/control/params.ts).

import type { NodeParams, PlanConnection } from "../core/plan";
import { SSMCS_INITIAL } from "../core/plan";

const EQ_FLAT = (): NodeParams["eqBands"] => [
  { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
  { on: true, q: 0.71, freq: 1000, gain: 0 },
  { on: true, q: 0.71, freq: 4000, gain: 0 },
  { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
];

const monoChannel = (): NodeParams => ({
  on: true,
  hpf: false,
  hpfFreq: 80,
  compEqType: 0,
  recPoint: 4,
  phase: false,
  gateOn: false,
  compOn: false,
  eqOn: true,
  eqOneKnob: { on: false, type: 0, level: 0 },
  eqBands: EQ_FLAT(),
  gate: { threshold: -50, range: -56, attack: 20.17, hold: 15.3, decay: 150.2 },
  comp: {
    threshold: -18,
    ratio: 3,
    gain: 2,
    attack: 34.58,
    release: 218,
    knee: 1,
    autoMakeup: false,
    oneKnob: false,
    oneKnobLevel: 0,
  },
  ssmcs: SSMCS_INITIAL,
  insertFx: -1,
  insertFxOn: false,
});

const stereoChannel = (): NodeParams => ({
  on: true,
  recPoint: 4,
  phaseL: false,
  phaseR: false,
  eqOn: true,
  eqOneKnob: { on: false, type: 0, level: 0 },
  eqBands: EQ_FLAT(),
});

const outputBus = (): NodeParams => ({
  level: 0,
  pan: 0,
  insertFx: -1,
  insertFxOn: false,
  eqOn: true,
  eqOneKnob: { on: false, type: 0, level: 0 },
  eqBands: EQ_FLAT(),
});

const ducker = (): NodeParams => ({
  duckerOn: false,
  ducker: { threshold: -40, range: -24, attack: 20.17, decay: 1000 },
});

export const URX22_NODE_PARAMS: Record<string, NodeParams> = {
  "in.micline_1_2": {
    gainL: -8,
    gainR: -8,
    phantomL: false,
    phantomR: false,
    clipSafeL: false,
    clipSafeR: false,
    hiZR: false,
  },
  "in.aux": { dGain: 0 },
  "in.usbmain_a": { dGain: -14 },
  "in.usbmain_b": { dGain: -14 },
  "in.usbmain_c": { dGain: -14 },
  "in.usbdaw_1_2": { dGain: 0 },
  "in.usbdaw_3_4": { dGain: 0 },
  "in.usbdaw_5_6": { dGain: 0 },
  "in.usbdaw_7_8": { dGain: 0 },
  "in.usbdaw_9_10": { dGain: 0 },
  "in.usbsub": { dGain: -14 },
  // Signal Type (stereoLink) / PAN-BAL live on the mono pair's primary (CH1) only.
  ch1: { ...monoChannel(), stereoLink: false, panBal: 0 },
  ch2: monoChannel(),
  ch_3_4: stereoChannel(),
  ch_5_6: stereoChannel(),
  ch_7_8: stereoChannel(),
  ch_9_10: stereoChannel(),
  "bus.stereo": { ...outputBus(), on: true },
  "bus.mix1": { ...outputBus(), busType: 0, on: true, panLink: false },
  "bus.mix2": { ...outputBus(), busType: 0, on: true, panLink: false },
  // FX channels ship ON at the factory (param 338, def 1) with their factory
  // effect program (FX1 = Rev-X Hall, FX2 = Mono Delay) — the FX channels are
  // identical across models.
  "bus.fx1": {
    on: true,
    fxEffect: {
      type: 0,
      params: {
        reverbTime: 23,
        revxInitialDelay: 2,
        decay: 27,
        roomSize: 29,
        revxDiffusion: 10,
        revxHpf: 4,
        revxLpf: 50,
        revxHiRatio: 8,
        lowRatio: 12,
        lowFreq: 32,
      },
    },
  },
  "bus.fx2": {
    on: true,
    fxEffect: {
      type: 1024,
      params: {
        delay: 5000,
        delayFeedback: 20,
        delayHiRatio: 7,
        delayHpf: 40,
        delayLpf: 110,
        sync: 0,
        bpm: 120,
        note: 9,
      },
    },
  },
  "out.ducker1": ducker(),
  "out.ducker2": ducker(),
  "out.ducker3": ducker(),
  "out.ducker4": ducker(),
  "bus.mon1": { level: 0, cueInterrupt: true, mono: false, phonesLevel: 2, on: true },
  "bus.mon2": { level: 0, cueInterrupt: true, mono: false, phonesLevel: 2, on: true },
  "bus.osc": { osc: { on: false, level: -14, mode: 0, freq: 1000, width: 0.1, interval: 1 } },
  // STREAMING DELAY factory state (off, 1.00 ms, 30 fps).
  "bus.stream": { delay: { on: false, time: 1, frameRate: 5 } },
};

const CHANNELS = ["ch1", "ch2", "ch_3_4", "ch_5_6", "ch_7_8", "ch_9_10"];

// CH SETTING colors: channels and FX buses Blue, MIX and STREAMING buses Orange,
// STEREO master Red. Hex are the COLOR_PALETTE entries (Blue 0 / Orange 1 / Red 6).
export const URX22_NODE_COLORS: Record<string, string> = {
  ...Object.fromEntries(CHANNELS.map((id) => [id, "#4a78c0"])),
  "bus.fx1": "#4a78c0",
  "bus.fx2": "#4a78c0",
  "bus.mix1": "#e8913a",
  "bus.mix2": "#e8913a",
  "bus.stream": "#e8913a",
  "bus.stereo": "#d9534f",
};

// CH SETTING names: mono channels ship "ch 1"/"ch 2", stereo channels their pair
// label " 3/ 4"…, buses their short labels. The leading space is the 2-character
// right-aligned channel numbering.
export const URX22_NODE_NAMES: Record<string, string> = {
  ch1: "ch 1",
  ch2: "ch 2",
  ch_3_4: " 3/ 4",
  ch_5_6: " 5/ 6",
  ch_7_8: " 7/ 8",
  ch_9_10: " 9/10",
  "bus.fx1": "FX 1",
  "bus.fx2": "FX 2",
  "bus.mix1": "MIX1",
  "bus.mix2": "MIX2",
  "bus.stream": "Strm",
  "bus.stereo": "ST",
};

const stereoSend = (from: string): PlanConnection => ({
  from: `${from}:out`,
  to: "bus.stereo:in",
  kind: "send",
  params: { level: 0, pan: 0 },
});

const mixSend = (from: string, mix: string): PlanConnection => ({
  from: `${from}:out`,
  to: `${mix}:in`,
  kind: "send",
  params: { level: -96.5, pan: 0, tap: "post" },
});

const fxSend = (from: string, fx: string): PlanConnection => ({
  from: `${from}:out`,
  to: `${fx}:in`,
  kind: "send",
  params: { level: -96.5, tap: "post" },
});

export const URX22_CONNECTIONS: PlanConnection[] = [
  ...CHANNELS.map(stereoSend),
  { from: "bus.fx1:out", to: "bus.stereo:in", kind: "send", params: { level: -96.5 } },
  { from: "bus.fx2:out", to: "bus.stereo:in", kind: "send", params: { level: -96.5 } },
  // FX channel → MIX sends ship ON at the factory (at -∞), like URX44V.
  { from: "bus.fx1:out", to: "bus.mix1:in", kind: "send", params: { level: -96.5, pan: 0, tap: "post" } },
  { from: "bus.fx1:out", to: "bus.mix2:in", kind: "send", params: { level: -96.5, pan: 0, tap: "post" } },
  { from: "bus.fx2:out", to: "bus.mix1:in", kind: "send", params: { level: -96.5, pan: 0, tap: "post" } },
  { from: "bus.fx2:out", to: "bus.mix2:in", kind: "send", params: { level: -96.5, pan: 0, tap: "post" } },
  ...CHANNELS.flatMap((ch) => [
    mixSend(ch, "bus.mix1"),
    mixSend(ch, "bus.mix2"),
    fxSend(ch, "bus.fx1"),
    fxSend(ch, "bus.fx2"),
  ]),
  { from: "ch1:out", to: "out.ducker1:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker2:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker3:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker4:in", kind: "key" },
  { from: "bus.osc:out", to: "bus.stereo:in", kind: "sendSwitch", params: { oscL: true, oscR: true } },
  { from: "in.micline_1_2:out", to: "ch1:in", kind: "source" },
  { from: "in.micline_1_2:out", to: "ch2:in", kind: "source" },
  // Stereo sources, copied by position from URX44V (see header): CH3/4=AUX,
  // CH5/6=USB MAIN A, CH7/8=USB MAIN B, CH9/10=USB MAIN C.
  { from: "in.aux:out", to: "ch_3_4:in", kind: "source" },
  { from: "in.usbmain_a:out", to: "ch_5_6:in", kind: "source" },
  { from: "in.usbmain_b:out", to: "ch_7_8:in", kind: "source" },
  { from: "in.usbmain_c:out", to: "ch_9_10:in", kind: "source" },
  { from: "bus.stereo:out", to: "bus.stream:in", kind: "source" },
  { from: "bus.stereo:out", to: "out.usbmain_a:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbmain_b:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbmain_c:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbsub:in", kind: "patch" },
  { from: "bus.stereo:out", to: "bus.mon1:in", kind: "source" },
  { from: "bus.stereo:out", to: "bus.mon2:in", kind: "source" },
  { from: "bus.stereo:out", to: "out.main:in", kind: "patch" },
];
